// Pick Three data for one lineup week: the week's games (for the locks and the
// picker), the signed-in user's own entries for that week and the one before
// it (so the card can show last week's score after the week flips), and the
// teams map with live points.
//
// Live points: the teams map in AuthContext loads once, at sign in, so on a
// Saturday its weekly points would sit still while games are played. While a
// game is running or about to (anyGameSoon, the same rule the lineup and the
// scoreboard poll on), this refetches the week's games and every team's
// weekly_points every POLL_MS, and lays the fresh points over the teams map it
// returns. Outside game time it fetches nothing.
//
// Writes go through the save_pick_three RPC (migration 082), which is the only
// way into the table and checks the lock itself.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabase/supabase";
import { useAuth } from "../context/AuthContext";
import { fetchWeekGames } from "../utils/weekGames";
import { anyGameSoon, POLL_MS } from "../utils/gameStatus";
import { SEASON_YEAR } from "../utils/season";
import { withLivePoints } from "../games/pickThree";

/**
 * `week` is the lineup week, or null (nothing loads). `finished` marks a week
 * behind the current one, whose games can be cached for a day.
 *
 * `entries` is null while loading, then `{ [week]: { teams, captain } }`.
 * `teams` is the AuthContext teams map with live weekly points laid over it:
 * score from this, not from useAuth().teams.
 */
export function usePickThree(week, { finished = false } = {}) {
  const { currentUser, teams: bootTeams } = useAuth();
  const userId = currentUser?.id;
  const [rows, setRows] = useState(null);
  const [entries, setEntries] = useState(null);
  const [failed, setFailed] = useState(false);
  const [livePoints, setLivePoints] = useState(null); // { slug: weeklyPoints }

  useEffect(() => {
    if (week === null) return undefined;
    let cancelled = false;
    setRows(null);
    fetchWeekGames(week, { isFinished: finished })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [week, finished]);

  useEffect(() => {
    if (week === null || !userId) return undefined;
    let cancelled = false;
    setFailed(false);
    supabase
      .from("pick_three_entries")
      .select("week, teams, captain")
      .eq("user_id", userId)
      .in("week", [week - 1, week])
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setFailed(true); setEntries({}); return; }
        setEntries(Object.fromEntries((data || []).map((row) => [row.week, { teams: row.teams, captain: row.captain }])));
      });
    return () => { cancelled = true; };
  }, [week, userId]);

  // The latest rows, for the poll to read without restarting on every update.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    if (week === null || finished) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (document.visibilityState === "hidden" || !anyGameSoon(rowsRef.current)) return;
      const [games, stats] = await Promise.all([
        fetchWeekGames(week).catch(() => null),
        supabase.from("team_season_stats").select("team_id, weekly_points").eq("season_year", SEASON_YEAR),
      ]);
      if (cancelled) return;
      if (games?.length) setRows(games);
      if (!stats.error && stats.data) {
        setLivePoints(Object.fromEntries(stats.data.map((r) => [r.team_id, r.weekly_points || {}])));
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
    // Restarted once when the first rows arrive, so the first check can see
    // them; after that the rows are read through rowsRef.
  }, [week, finished, rows === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const teams = useMemo(() => withLivePoints(bootTeams, livePoints), [bootTeams, livePoints]);

  /** Saves the picks and captain. Resolves to null, or the message to show the player. */
  const save = useCallback(async (picks, captain) => {
    const { error } = await supabase.rpc("save_pick_three", { p_teams: picks, p_captain: captain });
    if (error) return error.message || "Picks could not be saved. Try again.";
    setEntries((prev) => ({ ...(prev || {}), [week]: { teams: picks, captain } }));
    return null;
  }, [week]);

  return { rows, entries, failed, save, teams };
}
